/**
 * Commercial & Licensing — 4 templates.
 *
 * The paper a brand puts in front of the people it buys from, sells to and licenses with:
 * its own purchase order terms, its retail vendor terms, its wholesale terms of sale, and a
 * trademark licence.
 */

export const COMMERCIAL_TEMPLATES = [
  // ───────────────────────────────────────────────────────────────────────────────────────
  // 1. PURCHASE ORDER TERMS AND CONDITIONS
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "purchase-order-terms",
    docId: "purchase-order-terms",
    title: "PURCHASE ORDER TERMS AND CONDITIONS",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are buying ingredients, packaging or services on purchase orders and there is no master agreement behind them. Attach these terms to every order you issue, and get them signed by the suppliers you buy from repeatedly.",
      whyItMatters:
        "Most CPG buying happens on purchase orders, and most purchase orders travel with whatever terms the supplier attaches to its acknowledgment. Which set governs when they conflict is a genuinely contested question that turns on the documents and the sequence, and it is worth asking counsel about once. Either way, without your own terms the working assumption is that you bought on theirs: a short rejection window, no documentation requirement, and no consequence for a late delivery that costs you a booked production slot.",
      negotiateCarefully: [
        {
          point: "When the inspection clock starts",
          detail:
            "A window running from delivery expires long before the ingredient is opened at your co-packer, which is where a defect is actually found. Tie it to first use or opening, and keep a separate window for defects nobody could have seen.",
        },
        {
          point: "Documentation as a condition of acceptance",
          detail:
            "A certificate of analysis, lot documentation and an allergen statement should arrive with the shipment. Make missing paperwork a reason to reject and a reason the inspection clock has not started, or you will be chasing documents a retailer is already asking you for.",
        },
        {
          point: "Minimum remaining shelf life on receipt",
          detail:
            "Short-dated ingredient and finished goods deliveries are a recurring problem, and suppliers vary widely unless told. Put a minimum on every order for anything dated.",
        },
        {
          point: "Firm price after acceptance",
          detail:
            "Long-lead components can be repriced between order and delivery if nothing stops it. Say the price is firm through delivery and that surcharges not on the order are not payable.",
        },
        {
          point: "Symmetry on cancellation and change",
          detail:
            "Suppliers' forms often let them cancel freely while charging you to change an order. Decide what you will actually pay on cancellation — delivered goods plus documented committed materials, after mitigation — and say so.",
        },
      ],
      redFlags: [
        "Purchase orders issued with no terms attached at all.",
        "Rejection windows measured in a few days from delivery.",
        "No documentation requirement for ingredient or packaging shipments.",
        "Delivery dates described as estimates only, with no remedy for a late or short shipment.",
        "A supplier free to reprice or add surcharges after accepting the order.",
      ],
      timeToComplete: "20 minutes to fill · attach to every order once approved",
    },

    placeholders: [
      { token: "[TERMS EFFECTIVE DATE]", label: "Date these terms take effect", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[SUPPLIER NAME]", label: "Supplier's legal name (for countersignature)", example: "Great Lakes Ingredients, Inc." },
      { token: "[ACCEPTANCE PERIOD]", label: "Time for a supplier to reject an order", example: "3 business days" },
      { token: "[DELIVERY WINDOW]", label: "Acceptable delivery window", example: "the delivery date, or the two business days before it" },
      { token: "[DELIVERY TERMS]", label: "Delivery terms", example: "delivered duty paid to the destination on the order" },
      { token: "[MINIMUM SHELF LIFE ON DELIVERY]", label: "Minimum remaining shelf life on receipt", example: "85% of total shelf life" },
      { token: "[INSPECTION PERIOD]", label: "Inspection and rejection window", example: "30 days" },
      { token: "[LATENT DEFECT PERIOD]", label: "Window for defects not discoverable on inspection", example: "the shelf life of the goods" },
      { token: "[CHANGE RESPONSE PERIOD]", label: "Time for a supplier to price a change", example: "5 business days" },
      { token: "[CANCELLATION NOTICE]", label: "Notice to cancel an order", example: "15 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 45 days from the later of receipt of goods and receipt of a correct invoice" },
      { token: "[INVOICE DISPUTE PERIOD]", label: "Window to dispute an invoice", example: "30 days" },
      { token: "[WARRANTY PERIOD]", label: "Warranty period", example: "the shelf life of the goods, or 12 months for non-dated goods" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long the supplier keeps records", example: "shelf life plus 2 years" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `These Purchase Order Terms and Conditions (these "Terms") apply to every purchase order (each,
an "Order") issued on or after [TERMS EFFECTIVE DATE] by [COMPANY NAME], of [COMPANY ADDRESS]
("Buyer"), for goods, materials or services (the "Goods") used in the manufacture, packaging,
storage or sale of the [PRODUCT CATEGORY] products Buyer sells under the [BRAND NAME] brand.

1. APPLICATION AND ACCEPTANCE
1.1 These Terms are part of every Order and are incorporated into it by reference. An Order is
accepted, and a binding contract on these Terms is formed, on the earliest of the supplier's
written acceptance, its failure to reject the Order in writing within [ACCEPTANCE PERIOD], or
its commencement of performance or shipment against the Order.
1.2 The supplier shall acknowledge each Order in writing, confirming the quantity, price and
delivery date. If the supplier cannot meet the delivery date, it shall say so in the
acknowledgment rather than accept and deliver late.

2. PRECEDENCE OVER SUPPLIER FORMS
2.1 Any additional or different terms appearing in a supplier's quotation, acknowledgment,
confirmation, invoice, delivery note, packing slip, portal, click-through or standard conditions
are rejected and do not form part of the Order, whenever they are sent. Buyer's acceptance of
Goods, payment of an invoice, or silence in response to a supplier form is not agreement to
those terms. A term of an Order may be varied only in a writing signed by an authorised
representative of Buyer.
2.2 Where the parties have signed a master supply agreement covering the Goods, that agreement
controls and these Terms apply only to the extent they do not conflict with it.

3. PRICE
3.1 The price stated on the Order is firm through delivery and is not subject to increase for
any reason, including changes in the supplier's cost of materials, labour, energy, freight or
duties, unless Buyer agrees to the increase in writing before shipment. Prices include packing,
marking and, unless the Order states otherwise, freight to the delivery location.
3.2 No surcharge, minimum order fee, expedite fee, fuel charge or accessorial charge is payable
unless it appears on the Order.

4. DELIVERY, PACKING AND ROUTING
4.1 Time is of the essence. Goods shall be delivered on the date stated on the Order, within
[DELIVERY WINDOW], to the location and by the routing the Order specifies. Delivery terms are
[DELIVERY TERMS].
4.2 Goods shall be packed, palletised, marked and labelled to the receiving standards of the
delivery location, which for Goods delivered to a co-packer or third-party warehouse are that
site's published receiving requirements. Each pallet and case shall be marked with the Order
number, the item description, the quantity, the lot code and the production or pack date.
4.3 If the supplier expects to be late or short, it shall notify Buyer immediately. Buyer may
then cancel the affected quantity without charge, require expedited shipment at the supplier's
cost, or purchase substitute goods and recover from the supplier the reasonable excess cost of
doing so.

5. DOCUMENTATION AND REMAINING SHELF LIFE
5.1 Each shipment shall be accompanied by a packing list referencing the Order, and by the
certificate of analysis, specification sheet, allergen statement, lot documentation and any
certification documents the Order or the applicable specification requires. Goods delivered
without the required documentation may be rejected, and the inspection period does not begin
until the documentation is received.
5.2 Dated Goods shall have at least [MINIMUM SHELF LIFE ON DELIVERY] remaining on receipt.

6. INSPECTION, ACCEPTANCE AND REJECTION
6.1 Buyer may inspect, test and reject Goods that do not conform to the Order, the specification
or any applicable sample within [INSPECTION PERIOD], and payment before inspection is not
acceptance. Because ingredients and components are often first opened at a manufacturing site,
the inspection period runs from the later of receipt at the delivery location and first use or
opening of the Goods.
6.2 Buyer may reject Goods at any time within [LATENT DEFECT PERIOD] for a defect that was not
reasonably discoverable on inspection.
6.3 Rejected Goods are held at the supplier's risk and expense. At Buyer's election the supplier
shall replace them promptly at no charge or refund the price, and shall bear the cost of freight
in both directions, storage, sorting and disposal. Buyer may deduct amounts owed under this
Section from any sum payable to the supplier, on notice describing the basis.

7. TITLE AND RISK OF LOSS
7.1 Title and risk of loss pass to Buyer on delivery to the location stated on the Order in
accordance with the delivery terms, and in any event only on acceptance where Goods are
rejected. Property in any materials, tooling, artwork, dies or plates Buyer supplies or pays for
remains with Buyer, and the supplier shall identify them as Buyer's property, use them only for
Buyer's Goods, keep them insured, and return them on request.

8. CHANGES
8.1 Buyer may direct changes to the quantity, specification, packaging, delivery date or
destination of an Order. The supplier shall respond within [CHANGE RESPONSE PERIOD] with any
adjustment to price or delivery date required by the change, supported by documentation, and
shall continue performing the unchanged parts of the Order in the meantime. A price or schedule
adjustment is effective only when confirmed on a revised Order.
8.2 The supplier shall not change the specification, formulation, source, manufacturing site or
packaging of the Goods without Buyer's prior written approval.

9. CANCELLATION
9.1 Buyer may cancel an Order in whole or in part on [CANCELLATION NOTICE] written notice. On
cancellation Buyer shall pay for conforming Goods already delivered and, where the supplier has
begun manufacture, the documented cost of work in progress and materials properly committed to
the Order that the supplier cannot reasonably use or return, provided the supplier first uses
commercially reasonable efforts to mitigate.
9.2 Buyer may cancel without charge if the supplier fails to deliver on time, delivers
non-conforming Goods, becomes insolvent, or fails to perform any material obligation.

10. INVOICING AND PAYMENT
10.1 Invoices shall reference the Order number and the delivery documents and shall be sent to
the address on the Order. Correct invoices are payable [PAYMENT TERMS]. Buyer may dispute an
invoice within [INVOICE DISPUTE PERIOD] and may withhold the disputed amount while paying the
balance.

11. WARRANTIES
11.1 The supplier warrants that the Goods will conform to the Order, the specification and any
sample; will be new, of merchantable quality and free from defects in material and workmanship
for [WARRANTY PERIOD]; will be produced, handled and stored under the supplier's own food
safety, sanitation and quality programs where applicable; will be free of any lien or
encumbrance; and will not infringe the intellectual property rights of any third party.
11.2 The supplier further warrants that it complies with all laws applicable to it in producing,
handling, labelling, selling and delivering the Goods, and that it will provide on request the
documentation Buyer reasonably needs to satisfy its own obligations and its customers' vendor
requirements. These warranties survive inspection, acceptance and payment.

12. INDEMNITY
12.1 The supplier shall defend and indemnify Buyer and its officers, employees, customers and
agents against third-party claims, and the resulting losses, damages, costs and reasonable legal
fees, arising from the Goods, from the supplier's breach of these Terms or of a warranty, from
its negligence or wilful misconduct, or from an allegation that the Goods infringe a third
party's intellectual property rights. Buyer shall give prompt notice of a claim and allow the
supplier to control the defence with counsel it selects, and the supplier shall not settle in a
way that imposes an obligation or admission on Buyer without Buyer's written consent.

13. INSURANCE
13.1 The supplier shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] and product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], together with workers compensation and any coverage its
operations require. It shall name Buyer as an additional insured with respect to the Goods,
provide certificates of insurance on request, and give at least [INSURANCE NOTICE PERIOD]
written notice before a policy is cancelled, is not renewed or is materially reduced. Insurance
does not limit the supplier's obligations under these Terms.

14. CONFIDENTIALITY
14.1 The supplier shall keep confidential all non-public information it receives from Buyer,
including specifications, formulas, ingredient identities and sources, artwork, volumes,
forecasts and pricing; shall use it only to perform the Order; and shall not disclose it to any
third party without Buyer's written consent. These obligations continue for
[CONFIDENTIALITY YEARS] years after the Order is completed and, for information that constitutes
a trade secret, for as long as it remains a trade secret. The supplier shall not use Buyer's
name, marks or the existence of the relationship in any marketing material, customer list or
public statement without Buyer's prior written consent.

15. COMPLIANCE AND RECORDS
15.1 The supplier shall keep records of production, lot traceability, testing and shipment for
the Goods for [RECORD RETENTION PERIOD] and shall provide copies to Buyer on request. It shall
notify Buyer promptly of any deviation, hold, recall, withdrawal, certification change or
regulatory finding affecting Goods supplied to Buyer, and shall cooperate with any trace
exercise, withdrawal or recall Buyer conducts, including by identifying the lots supplied and
the shipments in which they were sent.

16. GENERAL
16.1 The parties are independent contractors. The supplier shall not assign an Order or
subcontract its performance without Buyer's prior written consent, and Buyer may assign an Order
to an affiliate or to a successor in a merger or sale of substantially all of its assets.
16.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance; payment
obligations are not excused, and Buyer may cancel affected quantities without charge and source
elsewhere while the event continues.
16.3 Notices must be in writing. Buyer's failure to enforce a provision is not a waiver of it,
and no remedy is exclusive of any other remedy available to Buyer. If any provision is held
unenforceable, the remainder continues in effect. Sections 6, 7, 11, 12, 13, 14, 15 and 16
survive completion, cancellation or termination of an Order.
16.4 These Terms and each Order are governed by the laws of the State of [GOVERNING LAW STATE],
without regard to its conflict of laws principles, and the parties consent to venue in [VENUE].

ACKNOWLEDGED AND AGREED. Where Buyer asks a supplier to countersign these Terms, they apply to
every Order between the parties from the date signed below.


[COMPANY NAME]                            [SUPPLIER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 2. RETAIL VENDOR AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "retail-vendor-agreement",
    docId: "retail-vendor-agreement",
    title: "RETAIL VENDOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "A retailer has accepted your item and onboarding paperwork is arriving. Use this as your own starting position, and as the checklist you read the retailer's vendor agreement, deduction schedule and routing guide against.",
      whyItMatters:
        "The purchase order is not the deal. The vendor agreement, the deduction schedule and the routing guide are the deal, and they are where a profitable-looking order becomes a loss. At a large chain most of this is not negotiable and asking for redlines mostly spends goodwill, so the useful skill is reading the fee schedule closely enough to model your true net per case before you accept the order — and being willing to decline volume that does not clear.",
      negotiateCarefully: [
        {
          point: "A closed list of fees and deductions",
          detail:
            "Get every fee, allowance, deduction and penalty onto one exhibit, and say that anything not on it may not be taken. That single sentence converts an open-ended deduction schedule into a number you can model.",
        },
        {
          point: "Backup documentation before the money moves",
          detail:
            "A deduction without documentation identifying the invoice, the event and the calculation cannot be checked, and cannot be disputed. Require the backup with the deduction, and make an unsupported deduction repayable.",
        },
        {
          point: "A deadline for asserting deductions at all",
          detail:
            "Post-audit claims arriving years later land against a season you already spent. Set an outside date measured from the invoice, after which a deduction cannot be asserted, and treat audit claims for closed periods the same way.",
        },
        {
          point: "A dispute window that starts when you can actually use it",
          detail:
            "A short window running from the remittance expires while you are still waiting for backup. Run it from the later of the remittance and the documentation, put a response deadline on the retailer, and add an escalation step.",
        },
        {
          point: "What happens when the item is discontinued",
          detail:
            "A right to return all warehouse and store stock at your cost can make being delisted more expensive than never being listed. Agree the notice period and the disposition of remaining inventory before you launch.",
        },
      ],
      redFlags: [
        "Being asked to sign without receiving the routing guide, vendor manual and deduction schedule.",
        "An unrestricted right for the retailer to amend fees and terms at any time.",
        "Post-audit deduction rights extending years back with no cap.",
        "A dispute window shorter than the time it takes to obtain backup documentation.",
        "Open-ended markdown, spoilage or destroy-in-field allowances.",
        "Full return of all warehouse and store inventory on discontinuation at your cost.",
      ],
      timeToComplete: "60 minutes to fill · model your net per case before you sign anything",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[RETAILER NAME]", label: "Retailer's legal name", example: "Regional Grocers, Inc." },
      { token: "[RETAILER STATE]", label: "Retailer's state of formation", example: "Texas" },
      { token: "[RETAILER ENTITY TYPE]", label: "Retailer's entity type", example: "corporation" },
      { token: "[RETAILER ADDRESS]", label: "Retailer's address", example: "9 Commerce Plaza, Dallas, TX 75201" },
      { token: "[PRODUCT LIST]", label: "Products supplied", example: "Acme Sparkling Yuzu 12 oz, 12-count case" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "60 days" },
      { token: "[ORDER LEAD TIME]", label: "Order lead time", example: "14 days" },
      { token: "[DELIVERY WINDOW]", label: "On-time delivery window", example: "the requested delivery date or the day before it" },
      { token: "[MINIMUM SHELF LIFE ON DELIVERY]", label: "Minimum remaining shelf life on receipt", example: "75% of total shelf life" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from receipt of a correct invoice" },
      { token: "[INVOICE DISPUTE PERIOD]", label: "Retailer's window to dispute an invoice", example: "30 days" },
      { token: "[DEDUCTION BACKUP PERIOD]", label: "Time to provide deduction documentation", example: "10 business days" },
      { token: "[DEDUCTION ASSERTION DEADLINE]", label: "Outside date for asserting any deduction", example: "180 days of the invoice date" },
      { token: "[DEDUCTION DISPUTE PERIOD]", label: "Your window to dispute a deduction", example: "90 days" },
      { token: "[DEDUCTION RESPONSE PERIOD]", label: "Retailer's window to answer a dispute", example: "20 business days" },
      { token: "[DEDUCTION ESCALATION PERIOD]", label: "Period before escalation", example: "15 business days" },
      { token: "[RETURN AUTHORIZATION PERIOD]", label: "Window to request a return authorisation", example: "10 days of receipt" },
      { token: "[PROMOTION NOTICE PERIOD]", label: "Notice before a promotion starts", example: "30 days" },
      { token: "[PROMOTION RECONCILIATION PERIOD]", label: "Time to reconcile a promotion", example: "60 days" },
      { token: "[TRACE RESPONSE TIME]", label: "Time to identify affected lots", example: "24 hours" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$2,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$5,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "1 year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice to stop automatic renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "90 days" },
      { token: "[DISCONTINUATION NOTICE]", label: "Notice before an item is discontinued", example: "60 days" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Retail Vendor Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS] ("Vendor"); and

  [RETAILER NAME], a [RETAILER STATE] [RETAILER ENTITY TYPE], with a principal place of
  business at [RETAILER ADDRESS] ("Retailer").

Vendor sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Retailer wishes to
purchase those products for resale. This Agreement sets the terms on which Vendor supplies them.

1. PRODUCTS AND PRICING
1.1 The products covered by this Agreement are [PRODUCT LIST] (the "Products"), at the prices
set out in Exhibit A. Exhibit A states, for each Product, the case cost, the case pack, and any
allowance included in that cost.
1.2 Vendor may change prices on [PRICE CHANGE NOTICE] written notice. Orders accepted before the
notice takes effect are filled at the price in effect when the order was accepted. Retailer may
add or remove Products only by agreement recorded in an amended Exhibit A.
1.3 Every fee, allowance, deduction, discount and charge that will be applied to Vendor's
invoices is listed in Exhibit B. A fee, allowance or deduction that is not in Exhibit B, or
agreed later in writing, may not be taken. Where Retailer maintains a vendor manual, routing
guide or supplier portal, Vendor is bound by it only in the version provided to Vendor in
writing, and a change to it applies to Vendor only from the date Retailer gives Vendor written
notice of the change.

2. PURCHASE ORDERS
2.1 Retailer orders by purchase order stating Product, quantity, price, delivery date and
destination, submitted at least [ORDER LEAD TIME] before the requested delivery date. Vendor
shall acknowledge each order and confirm the quantity it will ship.
2.2 The price on the order must match Exhibit A as amended. Where an order and this Agreement
conflict, this Agreement controls, and pre-printed terms on a purchase order, portal, invoice or
delivery document have no effect except as to Product, quantity, price, date and destination.

3. DELIVERY AND ROUTING
3.1 Vendor shall deliver within [DELIVERY WINDOW] of the requested delivery date, to the
destination and by the routing stated on the order, packed and palletised to the case pack and
pallet configuration in the applicable specification, and labelled with the item code, lot code
and date code that specification requires.
3.2 Dated Products shall have at least [MINIMUM SHELF LIFE ON DELIVERY] remaining on receipt.
Retailer may refuse product delivered with less, and shall tell Vendor before refusing so that
Vendor can arrange an alternative.
3.3 Where Vendor cannot ship an order in full, it shall notify Retailer before the ship date
with the quantity available and the date the balance will ship.

4. INVOICING AND PAYMENT
4.1 Vendor invoices on shipment, referencing the purchase order and the delivery documents.
Correct invoices are payable [PAYMENT TERMS]. Retailer shall pay the undisputed portion of an
invoice when due and may dispute an invoice within [INVOICE DISPUTE PERIOD], stating the basis.

5. DEDUCTIONS AND CHARGEBACKS
5.1 This Section governs every deduction, chargeback, offset, allowance recovery, penalty,
post-audit claim and short payment Retailer takes against Vendor, whatever it is called.
5.2 Retailer may take a deduction only where it is (a) provided for in Exhibit B or agreed in
writing, and (b) supported by documentation identifying the invoice, purchase order, delivery,
store or event it relates to, the amount, the basis for it, and the calculation. Retailer shall
provide that documentation with the deduction, or within [DEDUCTION BACKUP PERIOD] of Vendor's
request. A deduction for which the documentation is not provided within that period is reversed
and repaid.
5.3 Retailer shall assert any deduction within [DEDUCTION ASSERTION DEADLINE] of the invoice
date to which it relates. A deduction asserted after that deadline, including one arising from
an audit of a closed period, is not payable by Vendor.
5.4 Vendor may dispute a deduction within [DEDUCTION DISPUTE PERIOD] of the later of the date
the deduction appears on a remittance and the date Retailer provides the supporting
documentation. Retailer shall respond in writing within [DEDUCTION RESPONSE PERIOD], either
repaying the amount or stating why it stands and identifying the evidence relied on. If the
parties have not resolved the dispute within [DEDUCTION ESCALATION PERIOD] after Retailer's
response, either party may escalate it to a senior representative of each party, who shall
confer within a further such period before either party pursues any other remedy.
5.5 Vendor's failure to dispute a deduction within the period above does not waive a dispute
Vendor could not reasonably have raised because Retailer had not provided documentation.
Retailer shall not condition acceptance of future orders, listing, promotional participation or
payment of undisputed invoices on Vendor's withdrawal of a disputed deduction.
5.6 Vendor shall keep, and Retailer shall provide on request, records sufficient to reconcile
deductions against invoices, and each party shall reconcile the deduction account at least once
in each period for which Retailer prepares a statement.

6. RETURNS AND SPOILAGE
6.1 Retailer may return Product that is damaged in transit, delivered short-coded,
non-conforming to specification, or delivered in error, on notice given within
[RETURN AUTHORIZATION PERIOD] of receipt and with a return authorisation from Vendor. Vendor
pays freight on those returns and issues a credit.
6.2 Retailer may not return Product because it did not sell, unless the parties agree otherwise
in writing for a specific item or event.
6.3 Spoilage, unsaleable and damage allowances, if any, are stated in Exhibit B as a defined
rate applied to a defined base, and are reconciled against actual reported unsaleables at least
once in each period for which Retailer prepares a statement. An allowance is not a substitute
for a return authorisation and does not entitle Retailer to destroy Product in field without
recording the quantity and reason.
6.4 On discontinuation of a Product, Retailer shall give Vendor [DISCONTINUATION NOTICE] written
notice and the parties shall agree the disposition of remaining warehouse and store inventory
before any return is made.

7. PROMOTIONAL ALLOWANCES
7.1 Each promotion is agreed in a separate written document identifying the Products, the dates,
the performance Retailer will provide, the funding basis and rate, and the maximum amount Vendor
is committing. Vendor is not liable for promotional funding that exceeds that maximum or for a
promotion it did not agree in writing.
7.2 Retailer shall confirm each promotion at least [PROMOTION NOTICE PERIOD] before it begins
and shall reconcile it, with supporting scan or shipment data, within
[PROMOTION RECONCILIATION PERIOD] after it ends. Promotional deductions are subject to
Section 5.

8. PRODUCT QUALITY, RECALL AND WITHDRAWAL
8.1 Vendor is responsible for the Products conforming to their specification and for the
accuracy of their labelling. Retailer is responsible for handling, storing and rotating the
Products at its facilities and stores in accordance with the storage conditions on the pack.
8.2 Vendor has the right to decide to withdraw or recall its own Products. Each party shall
notify the other immediately on becoming aware of information suggesting a Product may be
unsafe, mislabelled or unfit for sale, and shall cooperate in any withdrawal or recall,
including by identifying affected lots and distribution within [TRACE RESPONSE TIME] of request.
Vendor controls communications about the Products with consumers and the media, and neither
party shall name the other in a public statement about a recall without the other's prior
written consent, except where required by law.
8.3 Recall costs are borne by the party whose act or omission caused the recall, and are shared
in proportion to contribution where both contributed. Retailer's recovery is limited to the cost
of the affected Product, the documented direct cost of removing and disposing of it, and
freight, unless the parties agree otherwise in writing.

9. INSURANCE
9.1 Vendor shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] and product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], shall name Retailer as an additional insured with respect to
the Products, shall provide certificates on request, and shall give at least
[INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced. Insurance does not limit either party's obligations under this Agreement.

10. INDEMNIFICATION
10.1 Each party shall defend and indemnify the other and its officers, employees and agents
against third-party claims, and the resulting losses, damages, costs and reasonable legal fees,
arising from its own breach of this Agreement, its negligence or wilful misconduct. Vendor's
indemnity covers claims that a Product as supplied was defective, unsafe or mislabelled, and
claims that a Product infringes a third party's intellectual property rights. Retailer's
indemnity covers claims arising from its handling, storage, repackaging or marketing of the
Products and from statements about the Products that Retailer made and Vendor did not approve.
The indemnified party shall give prompt notice, allow the indemnifying party to control the
defence, and cooperate at that party's cost.

11. CONFIDENTIALITY
11.1 Each party shall keep the other's non-public information confidential and use it only for
this relationship. Sales, inventory and promotional data Retailer provides about the Products
may be used by Vendor to plan production, forecast and service the account. Neither party shall
use the other's name or marks in advertising or public statements without prior written consent,
except that Retailer may advertise the Products it sells and Vendor may identify Retailer as a
customer in ordinary trade communications.

12. TERM AND TERMINATION
12.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing
automatically for successive periods of [RENEWAL TERM] unless either party gives written notice
of non-renewal at least [NON-RENEWAL NOTICE] before the end of the then-current term.
12.2 Either party may terminate on written notice if the other materially breaches and does not
cure within [CURE PERIOD] after written notice describing the breach, and may terminate
immediately if the other becomes insolvent or has a receiver appointed over a substantial part
of its assets. Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE]
written notice.
12.3 On termination, orders accepted before the notice are filled and paid for on these terms,
Retailer pays all undisputed amounts, and the parties reconcile the deduction account under
Section 5 within the periods stated there. Sections 5, 8, 9, 10, 11 and 13 survive.

13. GENERAL
13.1 The parties are independent contractors. Neither may assign this Agreement without the
other's prior written consent, except to a successor in a merger or a sale of substantially all
of its assets.
13.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance; payment
obligations are not excused.
13.3 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Exhibits, is the entire agreement between the parties on its subject matter
and may be amended only in a writing signed by both. A failure to enforce a provision is not a
waiver of it, and if any provision is held unenforceable the remainder continues in effect. This
Agreement may be signed in counterparts, including by electronic signature.
13.4 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without
regard to its conflict of laws principles. The parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [RETAILER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


EXHIBIT A — PRODUCTS AND PRICING. List each Product with its item code, case pack, case cost and
any allowance included in that cost.

EXHIBIT B — FEES, ALLOWANCES AND DEDUCTIONS. List every fee, allowance, deduction, penalty and
charge that may be applied to Vendor's invoices, with the rate, the base it is applied to and
when it is taken. Anything not listed here may not be deducted.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 3. WHOLESALE SUPPLY AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "wholesale-supply-agreement",
    docId: "wholesale-supply-agreement",
    title: "WHOLESALE SUPPLY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before you take your first wholesale order from a shop, a chain or a wholesaler. If you are currently selling on an emailed price list and someone else's purchase order, this is the document that is missing.",
      whyItMatters:
        "Without standing terms, every order runs on whatever is printed on the buyer's purchase order, and a buyer's PO terms are written entirely for the buyer: their payment terms, their right to reject, their return rights, their deduction schedule. Setting your own wholesale terms early is the cheapest leverage you will ever have, because once you are a small vendor at a large chain the terms stop being negotiable at all.",
      negotiateCarefully: [
        {
          point: "Say plainly that your terms prevail",
          detail:
            "One sentence rejecting the terms on the buyer's purchase order, portal and vendor manual, except as to product, quantity, price, date and destination, is the highest-value line in the document.",
        },
        {
          point: "Credit limit and the right to stop shipping",
          detail:
            "Long terms on a large opening order can consume the cash from a whole production run before a single invoice is paid. Set a limit, require prepayment for opening orders, and keep the right to hold shipments while a balance is overdue.",
        },
        {
          point: "Minimum remaining shelf life, in writing",
          detail:
            "Left undefined this becomes the buyer's judgement call, and a full truck can be refused on arrival for a code nobody told you about. State it as days or as a share of total shelf life.",
        },
        {
          point: "Returns limited to defective and non-conforming product",
          detail:
            "An open right to return unsold stock turns a booked sale into short-coded inventory coming back at your cost. Tie returns to a claim window and a return authorisation, and price restocking if you allow it at all.",
        },
        {
          point: "Buy-ahead before an announced price increase",
          detail:
            "A long notice requirement with no cap lets a buyer load up before every increase, so your cost rise lands months before your price rise does. Cap the quantity that can be ordered at the old price.",
        },
      ],
      redFlags: [
        "Silence on which terms govern when yours and the buyer's purchase order conflict.",
        "An unlimited right of return for unsold product.",
        "Deductions permitted with no documentation and no dispute process.",
        "No stated minimum remaining shelf life.",
        "Payment terms you cannot fund at the order size being discussed.",
        "Insurance limits or indemnities you have not checked against your actual policy.",
      ],
      timeToComplete: "45 minutes to fill · finalise before your first wholesale order",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[BUYER NAME]", label: "Buyer's legal name", example: "Northside Natural Foods, LLC" },
      { token: "[BUYER STATE]", label: "Buyer's state of formation", example: "Colorado" },
      { token: "[BUYER ENTITY TYPE]", label: "Buyer's entity type", example: "limited liability company" },
      { token: "[BUYER ADDRESS]", label: "Buyer's address", example: "77 Market St, Denver, CO 80202" },
      { token: "[PRODUCT LIST]", label: "Products supplied", example: "all Acme sparkling SKUs listed in Exhibit A" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "60 days" },
      { token: "[BUY AHEAD LIMIT]", label: "Cap on ordering ahead of an increase", example: "the buyer's average monthly volume over the prior three months" },
      { token: "[ORDER LEAD TIME]", label: "Order lead time", example: "10 business days" },
      { token: "[MINIMUM ORDER]", label: "Minimum order", example: "25 cases per SKU, 100 cases per order" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from invoice date" },
      { token: "[LATE PAYMENT RATE]", label: "Late payment charge", example: "1.5% per month on overdue undisputed amounts" },
      { token: "[CREDIT LIMIT]", label: "Credit limit", example: "$25,000 outstanding at any time" },
      { token: "[DELIVERY TERMS]", label: "Delivery terms", example: "FOB Seller's warehouse, freight prepaid and added" },
      { token: "[MINIMUM SHELF LIFE ON DELIVERY]", label: "Minimum remaining shelf life on receipt", example: "75% of total shelf life" },
      { token: "[INSPECTION PERIOD]", label: "Inspection window", example: "5 business days of receipt" },
      { token: "[CLAIM PERIOD]", label: "Outside date for claims", example: "30 days" },
      { token: "[DEDUCTION BACKUP PERIOD]", label: "Time to respond to a proposed deduction", example: "10 business days" },
      { token: "[DEDUCTION DISPUTE PERIOD]", label: "Window to raise a proposed deduction", example: "60 days" },
      { token: "[TERRITORY]", label: "Resale territory", example: "the states of Colorado and New Mexico" },
      { token: "[AUTHORIZED CHANNELS]", label: "Channels the buyer may sell through", example: "its own retail stores and its e-commerce site" },
      { token: "[EXCLUDED CHANNELS]", label: "Channels requiring consent", example: "online marketplaces, club, dollar and export" },
      { token: "[TRACE RESPONSE TIME]", label: "Time to identify lots and destinations", example: "24 hours" },
      { token: "[MARKETING SUPPORT]", label: "Marketing support provided", example: "point-of-sale kits, two demo days per store per year and approved brand assets" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "1 year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice to stop automatic renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "60 days" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Wholesale Supply Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS] ("Seller"); and

  [BUYER NAME], a [BUYER STATE] [BUYER ENTITY TYPE], with a principal place of business at
  [BUYER ADDRESS] ("Buyer").

Seller sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Buyer wishes to purchase
them at wholesale for resale. These are Seller's standing terms of sale.

1. PRODUCTS, PRICES AND PRICE CHANGES
1.1 The products covered by this Agreement are [PRODUCT LIST] (the "Products"), at the wholesale
prices in Exhibit A. Prices are exclusive of taxes and, unless Exhibit A states otherwise, of
freight.
1.2 Seller may change prices on [PRICE CHANGE NOTICE] written notice. Orders accepted before the
change takes effect are filled at the prior price. To prevent an announced increase being
defeated by forward buying, Buyer may order no more than [BUY AHEAD LIMIT] at the prior price
between the notice and the effective date.

2. ORDERS, MINIMUMS AND PRECEDENCE
2.1 Buyer orders in writing, stating Product, quantity, requested delivery date and destination,
at least [ORDER LEAD TIME] before the requested delivery date. The minimum order is
[MINIMUM ORDER]. Seller shall acknowledge each order and confirm the quantity and ship date, and
an order is binding when Seller acknowledges it.
2.2 This Agreement governs every order between the parties. Terms appearing on Buyer's purchase
order, vendor portal, supplier manual, invoice deduction schedule or other form do not apply and
are rejected, except as to Product, quantity, price, date and destination, unless Seller accepts
them in a writing signed by an authorised representative.

3. PAYMENT TERMS AND CREDIT
3.1 Correct invoices are payable [PAYMENT TERMS]. Undisputed amounts not paid when due bear a
charge of [LATE PAYMENT RATE]. Buyer's credit limit is [CREDIT LIMIT]; Seller may review it at
any time, may require prepayment or other assurance for an opening order or where the limit
would be exceeded, and may hold shipments while an undisputed balance is overdue.
3.2 Buyer shall pay each invoice in full and may not set off, deduct or withhold any amount
except as Section 6 allows.

4. DELIVERY, TITLE AND RISK OF LOSS
4.1 Delivery terms are [DELIVERY TERMS]. Title and risk of loss pass to Buyer in accordance with
those terms. Seller shall use reasonable efforts to ship complete and on the confirmed date, and
shall notify Buyer promptly of a short or delayed shipment with the quantity available and the
date the balance will ship.
4.2 Seller retains a security interest in Products delivered until they are paid for in full,
and Buyer shall sign any document reasonably needed to record it.

5. PRODUCT CONDITION, CODING AND SHELF LIFE
5.1 Products are supplied to the case pack, pallet configuration, case marking, lot coding and
date format stated in the applicable specification. Dated Products shall have at least
[MINIMUM SHELF LIFE ON DELIVERY] remaining on receipt.
5.2 Buyer shall store, handle and rotate the Products in accordance with the storage conditions
stated on the pack, shall rotate stock so that the oldest code ships first, and shall not sell
Product past its date code or in damaged, repacked or relabelled form.

6. INSPECTION, CLAIMS, RETURNS AND DEDUCTIONS
6.1 Buyer shall inspect each delivery and may reject Product that is short, damaged in transit,
short-coded or non-conforming by giving Seller written notice within [INSPECTION PERIOD] of
receipt, with the lot codes, quantities and photographs or other evidence. A claim made after
[CLAIM PERIOD] from delivery is waived, except for a defect that was not reasonably discoverable
on inspection.
6.2 Seller shall, at its election, replace properly rejected Product or issue a credit, and
shall pay freight on the return where the claim is Seller's responsibility. Product may not be
returned for any other reason, including because it did not sell, without Seller's prior written
return authorisation.
6.3 Buyer may deduct from an invoice only an amount Seller has credited in writing. Where Buyer
proposes any other deduction, chargeback or offset, it shall first give Seller documentation
identifying the invoice, delivery or event, the amount and the calculation, and Seller shall
respond within [DEDUCTION BACKUP PERIOD]. Buyer shall raise any proposed deduction within
[DEDUCTION DISPUTE PERIOD] of the invoice date to which it relates, after which it is waived.
Deductions taken without a written credit may be reinvoiced by Seller.

7. RESALE CONDITIONS AND CHANNELS
7.1 Buyer purchases for resale in [TERRITORY] through [AUTHORIZED CHANNELS]. Buyer shall not
resell the Products through [EXCLUDED CHANNELS] without Seller's prior written consent, and
shall not sell to a person Buyer knows or reasonably should know intends to resell outside the
authorised channels or territory.
7.2 Buyer shall resell the Products only in Seller's original, unopened packaging, without
altering, removing or obscuring any label, lot code, date code or marking, and shall not create
bundles, multipacks or repacked units bearing the Products without Seller's prior written
consent. Buyer shall use the Products' images, claims and marketing copy only as Seller supplies
or approves them.
7.3 Buyer shall keep records identifying the lot codes it received and the customers, stores or
locations to which they were shipped, and shall provide them to Seller within
[TRACE RESPONSE TIME] of request.

8. PRICING POLICY
8.1 Buyer determines its own resale prices. Seller may from time to time adopt and publish a
unilateral policy stating the advertised pricing on which Seller chooses to continue supplying
resellers. Any such policy is a statement of the terms on which Seller will do business and is
not an agreement between the parties as to Buyer's resale prices, and nothing in this Agreement
obliges Buyer to resell at any particular price. Seller will provide any such policy in writing
before applying it.

9. MARKETING SUPPORT
9.1 Seller will provide the marketing support described in Exhibit B, which is
[MARKETING SUPPORT]. Support beyond Exhibit B, including demos, samples, free fills, advertising
contributions and promotional funding, is provided only where agreed in advance in writing
identifying the activity, the dates, the funding basis and the maximum amount Seller is
committing.
9.2 Buyer shall provide the sell-through, inventory or placement information Exhibit B
describes, so that both parties can measure whether the support worked.

10. TRADEMARKS AND BRAND USE
10.1 Seller grants Buyer a non-exclusive, non-transferable, revocable right to use Seller's
trademarks and approved product images solely to advertise and resell the Products during the
term. All goodwill from that use belongs to Seller. Buyer shall not register or attempt to
register any of Seller's marks, any confusingly similar mark, or any domain name or social
handle containing them, and shall stop using the marks on termination other than to sell through
inventory it already holds.

11. PRODUCT QUALITY, RECALL AND TRACEABILITY
11.1 Seller is responsible for the Products conforming to their specification and for the
accuracy of their labelling as supplied. Buyer is responsible for their handling, storage and
rotation after delivery.
11.2 Seller has the right to decide to withdraw or recall its own Products. Each party shall
notify the other immediately on becoming aware of information suggesting a Product may be
unsafe, mislabelled or unfit for sale, and Buyer shall cooperate fully in any withdrawal or
recall, including by identifying affected lots and their destinations within
[TRACE RESPONSE TIME], holding affected stock, and following Seller's instructions on
disposition. Seller controls communications about the Products with consumers and the media.

12. INSURANCE AND INDEMNITY
12.1 Seller shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] and product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], shall provide certificates on request, and shall give at least
[INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced. Where Buyer requires additional insured status, it is provided on Seller's
policy terms and to the extent Seller's policy permits.
12.2 Each party shall defend and indemnify the other and its officers, employees and agents
against third-party claims, and the resulting losses, damages, costs and reasonable legal fees,
arising from its own breach of this Agreement, negligence or wilful misconduct. Seller's
indemnity covers claims that a Product as supplied was defective, unsafe or mislabelled. Buyer's
indemnity covers claims arising from its handling, storage, repackaging, advertising or resale
of the Products and from statements about them that Seller did not approve. Neither party is
liable to the other for indirect, incidental, special or consequential damages or lost profits.

13. TERM AND TERMINATION
13.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing
automatically for successive periods of [RENEWAL TERM] unless either party gives written notice
of non-renewal at least [NON-RENEWAL NOTICE] before the end of the then-current term.
13.2 Either party may terminate on written notice if the other materially breaches and does not
cure within [CURE PERIOD] after written notice describing the breach, and may terminate
immediately if the other becomes insolvent or has a receiver appointed over a substantial part
of its assets. Seller may also suspend shipments while an undisputed balance is overdue. Either
party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written notice.
13.3 On termination Seller shall fill orders it has already acknowledged unless the termination
is for Buyer's breach or Buyer's account is overdue, Buyer shall pay all undisputed amounts, and
Buyer may sell through inventory it already holds in accordance with Sections 7 and 10.
Sections 6, 7, 10, 11, 12 and 14 survive.

14. GENERAL
14.1 The parties are independent contractors, and neither may bind the other or hold itself out
as its agent. Neither party may assign this Agreement without the other's prior written consent,
except to a successor in a merger or a sale of substantially all of its assets.
14.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance; payment
obligations are not excused.
14.3 Each party shall keep the other's non-public information confidential and use it only for
this relationship. Notices must be in writing and are effective on delivery to the addresses
above. This Agreement, with its Exhibits, is the entire agreement between the parties on its
subject matter and may be amended only in a writing signed by both. A failure to enforce a
provision is not a waiver of it, and if any provision is held unenforceable the remainder
continues in effect. This Agreement may be signed in counterparts, including by electronic
signature.
14.4 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without
regard to its conflict of laws principles. The parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [BUYER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


EXHIBIT A — PRODUCTS AND WHOLESALE PRICE LIST. List each Product with its item code, case pack,
wholesale case price, and whether freight is included.

EXHIBIT B — MARKETING SUPPORT. Describe the support Seller provides, what Buyer provides in
return, and the information Buyer will report back.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 4. TRADEMARK LICENSE AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "trademark-license-agreement",
    docId: "trademark-license-agreement",
    title: "TRADEMARK LICENSE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Someone else's name will appear on your product, or your name will appear on someone else's — a heritage brand extending into beverage, a restaurant name on a retail item, your own mark licensed into a category you will not make yourself.",
      whyItMatters:
        "The economics and the operations pull in opposite directions and the contract has to hold both. On the money side the rate is what gets negotiated and the definition of the royalty base is what decides the outcome: whether trade spend, slotting, spoils, free fills, returns and freight come off before the royalty is calculated is worth more than a point or two of rate. On the operating side, approval rights over artwork and product changes sit on top of a production calendar where line time is booked weeks out and label minimums have their own lead times, so an approval right with no response deadline is a right to stop a production run. And a minimum guarantee keeps accruing whether or not the product sells.",
      negotiateCarefully: [
        {
          point: "The definition of net sales",
          detail:
            "Name the permitted deductions line by line, and say what happens to deductions a retailer takes months after the sale. A royalty on gross shipments in a category with heavy trade spend means paying on revenue that was later deducted away.",
        },
        {
          point: "The minimum guarantee against a downside case",
          detail:
            "It accrues on a calendar, not on performance. Model it against losing a chain reset, and ask for a cure, a step-down, or conversion of exclusivity to non-exclusive rather than automatic termination on a miss.",
        },
        {
          point: "An approval turnaround with a consequence",
          detail:
            "Set a response period and a deemed-approval outcome if it passes, plus a faster path for a change forced by a supply or specification problem. Without it, an unanswered email holds a booked co-packing slot and a label print window.",
        },
        {
          point: "A sell-off period sized to your packaging commitment",
          detail:
            "The print minimum on labels and cans is often larger than the finished goods. Make the sell-off long enough to clear both, and say how royalties and reporting work during it.",
        },
        {
          point: "Audit scope, lookback and who pays",
          detail:
            "An audit right is only useful if the agreement says how far back it reaches, how often it can happen, and who bears the cost when a shortfall is found. Leave all three unstated and the clause is either useless or open-ended.",
        },
      ],
      redFlags: [
        "A royalty base defined as gross sales with no deductions in a category with heavy trade spend.",
        "A minimum guarantee with no cure period and automatic termination on a shortfall.",
        "Approval rights with no response deadline.",
        "No sell-off period, or one measured in days.",
        "An audit right reaching back indefinitely with all costs on the licensee regardless of outcome.",
        "Silence on who carries recall cost when the mark is on the pack.",
      ],
      timeToComplete: "90 minutes to fill · model the guarantee before you agree it",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[LICENSOR NAME]", label: "Licensor's legal name", example: "Heritage Brands Holdings, Inc." },
      { token: "[LICENSOR STATE]", label: "Licensor's state of formation", example: "New York" },
      { token: "[LICENSOR ENTITY TYPE]", label: "Licensor's entity type", example: "corporation" },
      { token: "[LICENSOR ADDRESS]", label: "Licensor's address", example: "400 Park Ave, New York, NY 10022" },
      { token: "[LICENSEE NAME]", label: "Licensee's legal name", example: "Acme Beverage Co., LLC" },
      { token: "[LICENSEE STATE]", label: "Licensee's state of formation", example: "Delaware" },
      { token: "[LICENSEE ENTITY TYPE]", label: "Licensee's entity type", example: "limited liability company" },
      { token: "[LICENSEE ADDRESS]", label: "Licensee's address", example: "123 Main St, Austin, TX 78701" },
      { token: "[LICENSED MARKS]", label: "The marks being licensed", example: "the word mark HERITAGE and the accompanying crest logo" },
      { token: "[LICENSED PRODUCTS]", label: "Products the marks may appear on", example: "ready-to-drink canned sparkling beverages" },
      { token: "[TERRITORY]", label: "Territory", example: "the United States and Canada" },
      { token: "[CHANNELS]", label: "Permitted channels", example: "grocery, natural, convenience and the licensee's own e-commerce site" },
      { token: "[EXCLUSIVITY TYPE]", label: "Exclusive, sole or non-exclusive", example: "exclusive" },
      { token: "[GUARANTEE PERIOD]", label: "Length of a contract year", example: "12-month period" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "3 years" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "2 years" },
      { token: "[RENEWAL NOTICE]", label: "Notice to renew", example: "180 days" },
      { token: "[ROYALTY RATE]", label: "Royalty rate", example: "5%" },
      { token: "[PERMITTED DEDUCTIONS]", label: "Deductions allowed before royalty", example: "documented trade and promotional allowances, slotting, spoils and unsaleables, free fills, returns and credits, freight billed to the customer, and sales taxes" },
      { token: "[ADVANCE AMOUNT]", label: "Advance against royalties", example: "$25,000" },
      { token: "[MINIMUM GUARANTEE]", label: "Minimum guarantee per contract year", example: "$50,000 in year one, rising as set out in Exhibit A" },
      { token: "[REPORTING FREQUENCY]", label: "Reporting frequency", example: "quarterly" },
      { token: "[REPORT DUE DATE]", label: "When the report is due", example: "30 days" },
      { token: "[PAYMENT DUE DATE]", label: "When payment is due", example: "45 days" },
      { token: "[LATE PAYMENT RATE]", label: "Late payment charge", example: "1% per month" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long records are kept", example: "3 years after the end of the term" },
      { token: "[AUDIT FREQUENCY]", label: "How often the licensor may audit", example: "once in any 12-month period" },
      { token: "[AUDIT NOTICE]", label: "Notice required before an audit", example: "20 business days" },
      { token: "[AUDIT LOOKBACK]", label: "How far back an audit may reach", example: "the two preceding contract years" },
      { token: "[AUDIT SHORTFALL THRESHOLD]", label: "Shortfall that shifts audit cost", example: "5% of royalties due for the audited period" },
      { token: "[APPROVAL RESPONSE PERIOD]", label: "Licensor's approval turnaround", example: "10 business days" },
      { token: "[SAMPLE QUANTITY]", label: "Samples per submission", example: "12 units" },
      { token: "[SAMPLE FREQUENCY]", label: "How often samples are supplied", example: "on each first production run and annually thereafter" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$5,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[SELL-OFF PERIOD]", label: "Sell-off period after the term ends", example: "180 days" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Trademark License Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [LICENSOR NAME], a [LICENSOR STATE] [LICENSOR ENTITY TYPE], with a principal place of
  business at [LICENSOR ADDRESS] ("Licensor"); and

  [LICENSEE NAME], a [LICENSEE STATE] [LICENSEE ENTITY TYPE], with a principal place of
  business at [LICENSEE ADDRESS] ("Licensee").

Licensor owns the Licensed Marks. Licensee wishes to use them on the Licensed Products, and
Licensor is willing to permit that use subject to the quality controls and other terms below.

1. DEFINITIONS
1.1 "Licensed Marks" means [LICENSED MARKS]. "Licensed Products" means [LICENSED PRODUCTS].
"Territory" means [TERRITORY]. "Channels" means [CHANNELS]. "Net Sales" means Licensee's gross
invoiced sales of Licensed Products to unaffiliated customers, less the deductions permitted by
Section 5. "Contract Year" means each [GUARANTEE PERIOD] beginning on the Effective Date or an
anniversary of it.

2. GRANT AND SCOPE
2.1 Licensor grants Licensee a [EXCLUSIVITY TYPE], non-transferable right and licence to use the
Licensed Marks solely on and in connection with the manufacture, marketing, distribution and
sale of the Licensed Products, in the Territory, through the Channels, during the term.
2.2 Licensee shall not use the Licensed Marks on any other product, outside the Territory, or
through any channel not within the Channels, and shall not sell Licensed Products to a person
Licensee knows or reasonably should know intends to resell them outside the Territory or the
Channels.
2.3 Licensee may not sublicense the Licensed Marks. Licensee may have the Licensed Products
manufactured by a third-party manufacturer approved by Licensor in writing, provided Licensee
remains responsible for that manufacturer, binds it in writing to the quality and
confidentiality obligations of this Agreement, and requires it to make Licensed Products only
for Licensee.

3. RESERVATION AND EXCLUSIVITY
3.1 All rights not expressly granted are reserved to Licensor, including the right to use the
Licensed Marks on products, in territories and through channels outside the scope of the grant.
Where the grant is exclusive, Licensor shall not license the Licensed Marks to a third party for
the Licensed Products in the Territory in the Channels during the term, and any exclusivity is
conditional on Licensee meeting the minimum guarantee in Section 6.

4. TERM
4.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM] unless
terminated earlier. It renews for successive periods of [RENEWAL TERM] if Licensee has met the
minimum guarantee for the preceding Contract Year and gives written notice at least
[RENEWAL NOTICE] before the end of the then-current term, unless Licensor gives notice of
non-renewal in the same period.

5. ROYALTY AND NET SALES
5.1 Licensee shall pay Licensor a royalty of [ROYALTY RATE] of Net Sales of Licensed Products.
5.2 Net Sales are calculated after deducting only the following, in each case actually granted
or incurred and documented: [PERMITTED DEDUCTIONS]. No other deduction is permitted. Deductions
that are taken by a customer after the sale, including trade deductions, allowances and
chargebacks, are credited against Net Sales in the period in which they are taken, and Licensee
shall show them as a reconciling item in its royalty report.
5.3 A sale is made when the Licensed Product is invoiced. Licensed Products given away as
samples, free fills or promotional units carry no royalty, provided their quantity is reported
and is reasonable in relation to the promotional activity. Licensed Products sold to an
affiliate of Licensee are treated as sold at the price the affiliate charges an unaffiliated
customer.

6. ADVANCE AND MINIMUM GUARANTEE
6.1 Licensee shall pay Licensor an advance of [ADVANCE AMOUNT] on signing. The advance is
recoupable against royalties otherwise payable and is not refundable.
6.2 Licensee guarantees Licensor minimum royalties of [MINIMUM GUARANTEE] for each Contract
Year. If royalties actually accrued in a Contract Year are less than the guarantee, Licensee
shall pay the shortfall within [PAYMENT DUE DATE] after the end of that Contract Year. A
shortfall in one Contract Year is not carried forward against a later one, and an excess in one
Contract Year is not credited against a later guarantee unless the parties agree otherwise in
writing.

7. REPORTING AND PAYMENT
7.1 Licensee shall deliver a royalty report to Licensor [REPORTING FREQUENCY], within
[REPORT DUE DATE] after the end of each reporting period, showing by Licensed Product and by
Channel: units shipped, gross invoiced sales, each permitted deduction taken, Net Sales, the
royalty due, deductions taken by customers in the period, and a reconciliation to the amount
paid. Licensee shall deliver a report for each period whether or not any royalty is due.
7.2 Royalties are payable within [PAYMENT DUE DATE] after the end of each reporting period.
Amounts not paid when due bear a charge of [LATE PAYMENT RATE].

8. RECORDS AND AUDIT
8.1 Licensee shall keep complete books and records supporting each royalty report for
[RECORD RETENTION PERIOD]. Licensor may, [AUDIT FREQUENCY] and on [AUDIT NOTICE] written notice,
have an independent accountant examine those records during normal business hours, going back no
more than [AUDIT LOOKBACK].
8.2 Licensee shall pay any underpayment the audit identifies within [PAYMENT DUE DATE] of the
audit report. Licensor bears the cost of the audit, except that Licensee shall reimburse
Licensor's reasonable audit cost if the audit shows an underpayment for the audited period
exceeding [AUDIT SHORTFALL THRESHOLD]. The accountant shall report only the amounts in dispute
and shall treat Licensee's records as confidential.

9. QUALITY CONTROL AND APPROVALS
9.1 Licensed Products shall be of a quality at least equal to the quality standards Licensor
specifies and to the approved samples, and shall be manufactured, packaged, labelled, stored and
distributed in accordance with all laws applicable to Licensee.
9.2 Licensee shall submit to Licensor for prior written approval: the formulation and
specification of each Licensed Product; packaging, artwork, labels and inserts; advertising,
promotional and point-of-sale material; and any change to any of them. Licensor may approve,
reject with reasons, or request changes.
9.3 Licensor shall respond to each submission within [APPROVAL RESPONSE PERIOD]. If Licensor
does not respond within that period, the submission is deemed approved. Where a change is forced
by a supply interruption or a specification issue and Licensee identifies it as urgent, Licensor
shall use reasonable efforts to respond sooner, and the parties shall work in good faith to
avoid the loss of a booked production run or print window. An approval, once given, continues to
apply to that item until Licensor withdraws it on reasonable written notice, and the withdrawal
does not require Licensee to withdraw materials or inventory already produced.
9.4 Licensor may inspect any facility where Licensed Products are made, on reasonable written
notice and during normal business hours, at Licensor's cost.

10. SAMPLES
10.1 Licensee shall supply Licensor with [SAMPLE QUANTITY] production samples of each Licensed
Product [SAMPLE FREQUENCY], free of charge, and shall supply further samples on reasonable
request. Samples are for quality control and Licensor's internal reference and are not for
resale.

11. MARKING AND USE OF THE MARKS
11.1 Licensee shall use the Licensed Marks only in the form, style, colour and proportion
Licensor approves, shall apply the trademark notices and ownership legends Licensor specifies on
Licensed Products and marketing materials, and shall not combine the Licensed Marks with any
other mark without Licensor's prior written approval.
11.2 Licensee shall not use, register or attempt to register the Licensed Marks, any confusingly
similar mark, or any domain name, social handle or company name containing them, and shall not
challenge Licensor's ownership of the Licensed Marks.

12. OWNERSHIP AND GOODWILL
12.1 Licensor owns the Licensed Marks and all goodwill in them. All use of the Licensed Marks by
Licensee, and all goodwill generated by that use, inures solely to Licensor's benefit. Licensee
acquires no right in the Licensed Marks other than the licence granted here.
12.2 Artwork, packaging designs and other materials created for the Licensed Products that
incorporate or are derived from the Licensed Marks are Licensor's property, and Licensee assigns
them to Licensor and shall execute any document reasonably needed to record that. Licensee
retains ownership of its own formulations, processes and manufacturing know-how, and of any
material it created that does not incorporate or derive from the Licensed Marks.

13. INFRINGEMENT
13.1 Each party shall notify the other promptly on becoming aware of any unauthorised use of the
Licensed Marks or of any claim that use of the Licensed Marks infringes a third party's rights.
13.2 Licensor has the first right, at its cost, to bring or settle any action about the Licensed
Marks, and Licensee shall cooperate at Licensor's expense. If Licensor does not act within a
reasonable period after notice, and the infringement is material to Licensee's business in the
Territory, the parties shall confer on what to do. Licensee shall not bring an action about the
Licensed Marks without Licensor's prior written consent. Recoveries are applied first to the
costs of the party bringing the action, and the balance is shared as the parties agree in
writing before the action begins.

14. REPRESENTATIONS, INDEMNITY AND INSURANCE
14.1 Licensor represents that it owns or controls the Licensed Marks and has the right to grant
this licence, and shall notify Licensee promptly of anything that materially affects that.
Licensee represents that it has the capability to manufacture, market and distribute the
Licensed Products and shall do so in accordance with all laws applicable to it.
14.2 Licensee shall defend and indemnify Licensor against third-party claims arising from the
Licensed Products, from Licensee's breach of this Agreement, or from Licensee's negligence or
wilful misconduct, other than claims that the Licensed Marks themselves infringe a third party's
rights. Licensor shall defend and indemnify Licensee against third-party claims that use of the
Licensed Marks as authorised here infringes a third party's rights. The indemnified party shall
give prompt notice, allow the indemnifying party to control the defence, and cooperate at that
party's cost.
14.3 Licensee shall maintain product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], name Licensor as an additional insured, provide certificates on
request, and give at least [INSURANCE NOTICE PERIOD] written notice before a policy is
cancelled, is not renewed or is materially reduced.

15. RECALL
15.1 Licensee has operational responsibility for any recall or withdrawal of Licensed Products
and shall bear its cost, except to the extent the recall arises from something Licensor supplied
or required. Licensee shall notify Licensor immediately on becoming aware of information
suggesting a Licensed Product may be unsafe, mislabelled or unfit for sale, shall consult
Licensor before any public communication that names the Licensed Marks, and shall keep Licensor
informed while the event continues. Licensor may require Licensee to suspend use of the Licensed
Marks on affected inventory.

16. TERMINATION
16.1 Either party may terminate on written notice if the other materially breaches and does not
cure within [CURE PERIOD] after written notice describing the breach, or immediately if the
other becomes insolvent, makes a general assignment for the benefit of creditors, or has a
receiver appointed over a substantial part of its assets.
16.2 Licensor may also terminate if Licensee fails to pay a minimum guarantee shortfall when due
and does not cure within [CURE PERIOD]; uses the Licensed Marks outside the scope of the grant
and does not stop within [CURE PERIOD] of notice; sells Licensed Products that have not been
approved under Section 9; or undergoes a change of control to a competitor of Licensor, on
written notice given within a reasonable period after Licensor learns of it.
16.3 Where the grant is exclusive and Licensee misses the minimum guarantee, Licensor may elect,
instead of terminating, to convert the grant to non-exclusive on written notice.

17. SELL-OFF AND EFFECT OF TERMINATION
17.1 On expiry or termination other than for Licensee's uncured breach, Licensee may continue to
sell Licensed Products it holds in finished inventory, and Licensed Products it has committed to
produce from packaging already printed, for [SELL-OFF PERIOD], on a non-exclusive basis and
subject to every term of this Agreement, including royalties, reporting and quality control.
Licensee shall provide Licensor with a written inventory of finished goods and committed
packaging within a reasonable period after the end of the term.
17.2 At the end of the sell-off period Licensee shall stop using the Licensed Marks, destroy or
deliver to Licensor remaining packaging and marketing materials bearing them, and confirm in
writing that it has done so. Licensee shall pay all royalties accrued through the end of the
sell-off period, and any unpaid minimum guarantee for the final Contract Year, prorated where
the term ends part way through it.

18. GENERAL
18.1 The parties are independent contractors, and nothing creates a partnership, joint venture,
agency or employment relationship. Licensee may not assign this Agreement or delegate its
performance without Licensor's prior written consent, including in a change of control; Licensor
may assign it together with the Licensed Marks.
18.2 Each party shall keep the other's non-public information confidential and use it only to
perform this Agreement. Neither party shall issue a press release about this Agreement without
the other's prior written consent.
18.3 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance; payment
obligations are not excused.
18.4 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement is the entire agreement between the parties on its subject matter and may be amended
only in a writing signed by both. A failure to enforce a provision is not a waiver of it, and if
any provision is held unenforceable the remainder continues in effect. Sections 8, 12, 13, 14,
15, 17 and 18 survive termination. This Agreement may be signed in counterparts, including by
electronic signature.
18.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without
regard to its conflict of laws principles. The parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[LICENSOR NAME]                           [LICENSEE NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },
];

export default COMMERCIAL_TEMPLATES;
